<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MasifiSettingsAPI from 'dashboard/api/masifiSettings';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { useStore } from 'vuex';

// Sección unificada de settings propias de Masifi ("Masifi Inbox"). Cada knob del
// backend (Masifi::Settings::REGISTRY) se renderiza como una card. Hoy: aviso de duplicado.
const { t } = useI18n();
const store = useStore();

const isLoading = ref(true);
const duplicateOwnerAlert = ref(true);
const isolateLeads = ref(true);
const notifyNewConversation = ref(false);
const autoAcceptOnReply = ref(true);
const crmEnabled = ref(true);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await MasifiSettingsAPI.get();
    duplicateOwnerAlert.value = Boolean(
      data.settings?.duplicate_lead_owner_alert
    );
    isolateLeads.value = Boolean(data.settings?.isolate_leads_on_reassign);
    notifyNewConversation.value = Boolean(
      data.settings?.notify_on_new_conversation
    );
    autoAcceptOnReply.value = Boolean(data.settings?.auto_accept_on_reply);
    crmEnabled.value = Boolean(data.settings?.crm_enabled);
  } catch (error) {
    useAlert(t('MASIFI_SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Persiste un knob; revierte el toggle local si la API falla. Reusado por cada card.
const persist = async (key, value, model) => {
  try {
    await MasifiSettingsAPI.update({ [key]: value });
    useAlert(t('MASIFI_SETTINGS.API.SAVE_SUCCESS'));
  } catch (error) {
    model.value = !value; // revertir si falló
    useAlert(t('MASIFI_SETTINGS.API.SAVE_ERROR'));
  }
};

const updateDuplicateOwner = value =>
  persist('duplicate_lead_owner_alert', value, duplicateOwnerAlert);
const updateIsolateLeads = value =>
  persist('isolate_leads_on_reassign', value, isolateLeads);
const updateNotifyNewConversation = value =>
  persist('notify_on_new_conversation', value, notifyNewConversation);
const updateAutoAcceptOnReply = value =>
  persist('auto_accept_on_reply', value, autoAcceptOnReply);
const updateCrmEnabled = async value => {
  await persist('crm_enabled', value, crmEnabled);
  // Refresca el record de la cuenta en el store → el gate del sidebar (Policy feature-flag,
  // que lee account.features) re-evalúa crm_active? al instante, sin recargar la página.
  store.dispatch('accounts/get');
};

onMounted(fetchSettings);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('MASIFI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('MASIFI_SETTINGS.TITLE')"
        :description="t('MASIFI_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pb-8">
        <SettingsToggleSection
          v-model="crmEnabled"
          :header="t('MASIFI_SETTINGS.CRM_MODULE.HEADER')"
          :description="t('MASIFI_SETTINGS.CRM_MODULE.DESCRIPTION')"
          @update:model-value="updateCrmEnabled"
        />
        <SettingsToggleSection
          v-model="duplicateOwnerAlert"
          :header="t('MASIFI_SETTINGS.DUPLICATE_OWNER.HEADER')"
          :description="t('MASIFI_SETTINGS.DUPLICATE_OWNER.DESCRIPTION')"
          @update:model-value="updateDuplicateOwner"
        />
        <SettingsToggleSection
          v-model="isolateLeads"
          :header="t('MASIFI_SETTINGS.ISOLATE_LEADS.HEADER')"
          :description="t('MASIFI_SETTINGS.ISOLATE_LEADS.DESCRIPTION')"
          @update:model-value="updateIsolateLeads"
        />
        <SettingsToggleSection
          v-model="notifyNewConversation"
          :header="t('MASIFI_SETTINGS.NEW_CONVERSATION.HEADER')"
          :description="t('MASIFI_SETTINGS.NEW_CONVERSATION.DESCRIPTION')"
          @update:model-value="updateNotifyNewConversation"
        />
        <SettingsToggleSection
          v-model="autoAcceptOnReply"
          :header="t('MASIFI_SETTINGS.AUTO_ACCEPT.HEADER')"
          :description="t('MASIFI_SETTINGS.AUTO_ACCEPT.DESCRIPTION')"
          @update:model-value="updateAutoAcceptOnReply"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
